import { CaptureSlice, sliceCapture } from '../audio/capture_slice.ts';
import { CaptureRecording } from '../audio/learn_mic_capture.ts';

// "Hear mine" (issue #3722): the singer's take as the page keeps it, in memory only. The microphone
// runs with automatic gain control off, so a take can be quiet enough that its playback is hard to
// hear over the room (the plan's "Playback loudness"). The kept take is therefore scaled so its
// loudest sample sits near playbackPeak, by no more than playbackMaxGain, so a whisper is not blown
// up into hiss; a take whose peak is under playbackSilenceFloor is silence, and is left as it is.
// Nothing here stores or sends anything. Pure, so it is tested without audio.

export const playbackPeak = 0.8;
export const playbackMaxGain = 8;
export const playbackSilenceFloor = 0.01; // -40 dBFS: below it, nobody sang

// The samples scaled to the peak, as a copy; the same array when they are silence
export function normalizeTake(samples: Float32Array): Float32Array {
  let peak = 0;
  for (const sample of samples) {
    const level = Math.abs(sample);
    if (level > peak) {
      peak = level; // a NaN never compares greater, so it cannot set the gain
    }
  }
  if (!(peak >= playbackSilenceFloor)) {
    return samples;
  }
  const gain = Math.min(playbackMaxGain, playbackPeak / peak);
  return samples.map((sample) => sample * gain);
}

// The take cut from the rolling capture and made loud enough to hear; undefined when there is no
// capture or none of the take is in it
export function keptTakeOf(
  recording: CaptureRecording | undefined,
  startMs: number | undefined,
  endMs: number | undefined,
): CaptureSlice | undefined {
  if (!recording || startMs === undefined || endMs === undefined) {
    return undefined;
  }
  const slice = sliceCapture(recording, startMs, endMs);
  return slice && { ...slice, samples: normalizeTake(slice.samples) };
}
